/**
 * PaymentStatusBadge.tsx
 *
 * Single source of truth for rendering a payment row's status. Mirrors the
 * legacy `dt-full-payment-list-authnet.php` mapping: derives the label from
 * the latest `payment_transactions_authnet.status` plus the `payments.void`
 * flag and emits the real legacy statuses (Manually Entered, Pending,
 * Settled, Voided, Declined, Refund Pending, Refunded). Used by the All
 * Payments list, the patient Payments tab, and any other surface that
 * displays a payment's status.
 */
import { Badge } from '@/components/ui/Badge';
import {
  getPaymentStatusDisplay,
  type PaymentStatusDisplay,
} from '@/pages/payments/paymentsShared';
import type { Payment } from '@/services/payments.service';

interface PaymentStatusBadgeProps {
  payment: Pick<Payment, 'void' | 'transStatus' | 'isPaidByCredit'>;
  /** When the payment is voided, show the void date below the badge. */
  voidDate?: string | null;
  /** When the payment is voided, show the user who voided it below the badge. */
  voidUserName?: string | null;
  size?: 'sm' | 'md';
  className?: string;
}

function formatVoidDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function PaymentStatusBadge({
  payment,
  voidDate,
  voidUserName,
  size = 'sm',
  className,
}: PaymentStatusBadgeProps) {
  const display: PaymentStatusDisplay = getPaymentStatusDisplay(payment);
  const detail = display.voided ? formatVoidDate(voidDate) : '';
  const tooltip = display.paidByCredit
    ? 'The card charge was declined. A membership credit paid this month, so nothing is owed and no cash was received.'
    : display.voided && (detail || voidUserName)
      ? `Voided${detail ? ` ${detail}` : ''}${voidUserName ? ` by ${voidUserName}` : ''}`
      : undefined;

  return (
    <span className={className} title={tooltip}>
      <Badge variant={display.variant} size={size}>{display.label}</Badge>
    </span>
  );
}
